// State reducer + initial state + pipeline builder.
// Ported verbatim from broker-team-analyzer-preview.html.

import { BROKERAGES } from './brokerages';

export const uid = () => Math.random().toString(36).slice(2, 10);

export function makeSlot({
  is_current = false,
  brokerage_key = 'custom',
  name = '',
} = {}) {
  const b = BROKERAGES[brokerage_key];
  return {
    id: uid(),
    is_current,
    brokerage_key,
    name: name || b.label,
    ...b.defaults,
    already_paid_to_cap: 0,
    on_team: false,
    team_name: '',
    team_split_pct: 30,
    team_cap: 0,
    team_monthly_fee: 0,
    team_per_deal_fee: 0,
    waterfall: 'team_first',
    show_rev_share: false,
    rev_share_estimate: 0,
  };
}

export function buildStarterPipeline({
  count = 5,
  avgPrice = 400000,
  commPct = 2.5,
} = {}) {
  if (!count || count < 1) return [];
  const deals = [];
  for (let i = 0; i < count; i++) {
    const month = Math.floor((i * 12) / count);
    deals.push({
      id: uid(),
      nickname: 'Deal ' + (i + 1),
      side: i % 2 === 0 ? 'buyer' : 'listing',
      sale_price: avgPrice,
      commission_pct: commPct,
      referral_out_pct: 0,
      concessions: 0,
      close_month: month,
      tc_fees: 0,
    });
  }
  return deals;
}

export const initialState = {
  step: 0,
  currentSkipped: false,
  slots: [
    makeSlot({
      is_current: true,
      brokerage_key: 'custom',
      name: 'My Current Brokerage',
    }),
    makeSlot({ brokerage_key: 'exp', name: 'eXp Realty' }),
  ],
  deals: buildStarterPipeline(),
};

export function reducer(state, action) {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, step: action.step };
    case 'RESET_PIPELINE':
      return { ...state, deals: buildStarterPipeline() };
    case 'UPDATE_SLOT':
      return {
        ...state,
        slots: state.slots.map((s) =>
          s.id === action.id ? { ...s, ...action.patch } : s,
        ),
      };
    case 'SET_SLOT_BROKERAGE':
      return {
        ...state,
        slots: state.slots.map((s) => {
          if (s.id !== action.id) return s;
          const b = BROKERAGES[action.key];
          return {
            ...s,
            brokerage_key: action.key,
            name:
              s.name === BROKERAGES[s.brokerage_key].label ? b.label : s.name,
            ...b.defaults,
            already_paid_to_cap: s.already_paid_to_cap,
            on_team: s.on_team,
            team_name: s.team_name,
            team_split_pct: s.team_split_pct,
            team_cap: s.team_cap,
            team_monthly_fee: s.team_monthly_fee,
            team_per_deal_fee: s.team_per_deal_fee,
            waterfall: s.waterfall,
            is_current: s.is_current,
          };
        }),
      };
    case 'ADD_SLOT': {
      if (state.slots.filter((s) => !s.is_current).length >= 4) return state;
      return {
        ...state,
        slots: [
          ...state.slots,
          makeSlot({
            brokerage_key: 'custom',
            name: 'Option ' + state.slots.length,
          }),
        ],
      };
    }
    case 'REMOVE_SLOT':
      return {
        ...state,
        slots: state.slots.filter((s) => s.id !== action.id),
      };
    case 'SKIP_CURRENT':
      return {
        ...state,
        currentSkipped: action.value,
        slots: action.value
          ? state.slots.filter((s) => !s.is_current)
          : state.slots.find((s) => s.is_current)
          ? state.slots
          : [
              makeSlot({
                is_current: true,
                brokerage_key: 'custom',
                name: 'My Current Brokerage',
              }),
              ...state.slots,
            ],
      };
    case 'UPDATE_DEAL':
      return {
        ...state,
        deals: state.deals.map((d) =>
          d.id === action.id ? { ...d, ...action.patch } : d,
        ),
      };
    case 'ADD_DEAL':
      return {
        ...state,
        deals: [
          ...state.deals,
          {
            id: uid(),
            nickname: 'Deal ' + (state.deals.length + 1),
            side: 'buyer',
            sale_price: 400000,
            commission_pct: 2.5,
            referral_out_pct: 0,
            concessions: 0,
            close_month: 6,
            tc_fees: 0,
          },
        ],
      };
    case 'REMOVE_DEAL':
      return {
        ...state,
        deals: state.deals.filter((d) => d.id !== action.id),
      };
    default:
      return state;
  }
}
